import React, { useState } from 'react'
import { Box, Typography, TextField, Tooltip } from '@mui/material'
import type { TPPCell } from '../../TPPCell/queries/useTPPCellQuery'
import { ReactComponent as LinkingIcon } from '../../../../../assets/images/hyperlink.svg'
import { ReactComponent as TrashIcon } from '../../../../../assets/images/trash_icon.svg'
import { ReactComponent as SaveIcon } from '../../../../../assets/images/check-circle-1.svg'
import { ReactComponent as CancelIcon } from '../../../../../assets/images/Close.svg'

import TPPLinking from '../../../../../components/Linking/TPPLinking'

interface CellEditComponentProps {
    cell: TPPCell
    order: number
    onChange?: (cellId: string, value: string) => void
    onDelete?: (cellId: string) => void
}

const CellEditComponent: React.FC<CellEditComponentProps> = ({ cell, order, onChange, onDelete }) => {
    const isNewCell = cell._id.startsWith('new-')
    const [value, setValue] = useState<string>(cell.content || '')
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
    const [isLinkingOpen, setIsLinkingOpen] = useState(false)

    const handleTrashClick = () => {
        setIsConfirmingDelete(true)
    }

    const handleCancelDelete = () => {
        setIsConfirmingDelete(false)
    }

    const handleConfirmDelete = () => {
        setIsConfirmingDelete(false)
        if (onDelete) {
            onDelete(cell._id)
        }
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value
        setValue(newValue)
        if (onChange) {
            onChange(cell._id, newValue)
        }
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                }}
            >
                {/* Order number to the left of the text field box */}
                <Typography
                    variant="22x300"
                    sx={{
                        color: 'figma.grayscale.tier3',
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 22,
                        fontStyle: 'normal',
                        fontWeight: 300,
                        lineHeight: '150%', // 33px
                        // normalize alignment so multi-digit numbers don't push the text field
                        width: '44px',
                        textAlign: 'right',
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {order + '.'}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        padding: '16px',
                        alignItems: 'center',
                        flex: '1 0 0',
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'figma.grayscale.stroke',
                    }}
                >
                    <TextField
                        value={value}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                        sx={{
                            '& .MuiInputBase-root': {
                                paddingY: 0,
                                minHeight: '24px',
                            },
                        }}
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                flex: '1 0 0',
                                color: 'figma.grayscale.tier2',
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: 16,
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '1.3', // a bit tighter than 150%
                                padding: 0,
                            },
                        }}
                    />
                </Box>

                {/* Hyperlink button to the right of the text field */}
                <Tooltip title={isNewCell ? 'Save cell for linking' : ''} disableHoverListener={!isNewCell} arrow>
                    <Box
                        component="button"
                        type="button"
                        onClick={() => {
                            if (!isNewCell) {
                                setIsLinkingOpen(true)
                            }
                        }}
                        sx={{
                            width: '72px',
                            height: '32px',
                            borderRadius: '9px',
                            bgcolor: 'figma.primary',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            padding: 0,
                            opacity: isNewCell ? 0.5 : 1,
                            color: 'figma.grayscale.contentWrapper',
                        }}
                    >
                        <LinkingIcon
                            style={{
                                width: 20,
                                height: 20,
                            }}
                        />
                    </Box>
                </Tooltip>

                {/* Delete controls on the far right of the row */}
                {isConfirmingDelete ? (
                    <>
                        {/* Save (confirm delete) icon */}
                        <Box
                            component="button"
                            type="button"
                            onClick={handleConfirmDelete}
                            sx={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                background: 'transparent',
                                cursor: 'pointer',
                                color: 'figma.grayscale.tier2',
                                '&:hover': {
                                    color: 'figma.primary',
                                },
                            }}
                        >
                            <SaveIcon
                                style={{
                                    width: 20,
                                    height: 20,
                                }}
                            />
                        </Box>

                        {/* Cancel delete icon */}
                        <Box
                            component="button"
                            type="button"
                            onClick={handleCancelDelete}
                            sx={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                background: 'transparent',
                                cursor: 'pointer',
                                color: 'figma.grayscale.tier2',
                                '&:hover': {
                                    color: 'figma.primary',
                                },
                            }}
                        >
                            <CancelIcon
                                style={{
                                    width: 20,
                                    height: 20,
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        onClick={handleTrashClick}
                        sx={{
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            padding: 0,
                            margin: 0,
                            background: 'transparent',
                            cursor: 'pointer',
                            color: 'figma.grayscale.tier2',
                            '&:hover': {
                                color: 'figma.primary',
                            },
                        }}
                    >
                        <TrashIcon
                            style={{
                                width: 20,
                                height: 20,
                            }}
                        />
                    </Box>
                )}
            </Box>

            {isLinkingOpen && (
                <TPPLinking
                    type="tppCell"
                    id={cell._id}
                    previousDataStack={[]}
                    onClose={() => setIsLinkingOpen(false)}
                />
            )}
        </>
    )
}

export default CellEditComponent
